/**
 * Tactical placement and movement — the two-tier pair `move_token` (gameplay: turn-gated,
 * budget-metered, footprint-aware) and `place_token` (force-apply authoring), their teleport
 * peer, the stand-up verb, and the internal `restore_pos`/`restore_stand` inverses.
 *
 * A leaf-handler group of `./leafReducer`'s switch; see `../reducer` for the tier's contract.
 */

import { CommandError, err, ok, type Result } from "@lancer-vtt/shared";
import { anchorPathTo, cellRegion, moveCostTo, movePathCost } from "@lancer-vtt/space";
import { FLYING_STATUS, checkMovePath, defaultRouteContext, flightHeadingAllows, moveBudgetFor, movementContext, tetherBreaker, trampleRouteRefusal, voluntaryMoveBar } from "../movement";
import { placementFailure, type PlacementFailure } from "../placementFit";
import { DOWN_AND_OUT_STATUS, IMMOBILIZED_STATUS, MESMERIZED_STATUS, PRONE_STATUS, TETHER_RANGE, activeActor, onBracedTurn, cellGridMismatch, type Cell, type Combatant, encounterGridMode, hasStatus, heldAction, isStunned, recordSpend, type ActivationState, type EncounterState, type FlightHeading, type TurnBudget } from "../types";
import { worldFor } from "../world";
import { changed, requireCombatant, withCombatant, type EncounterHandler } from "./context";

/**
 * The parenthetical cause of an endpoint reject. Off-map and solid terrain are public, so they are
 * always named. `genericOccupied` is the phrase a player-reachable verb uses for `occupied` so a
 * reject never reveals a concealed unit; `null` names the occupant (GM-only verbs).
 */
function placementCauseText(world: ReturnType<typeof worldFor>, failure: PlacementFailure, genericOccupied: string | null): string {
  const at = world.key(failure.cell);
  switch (failure.cause) {
    case "off-map":
      return `footprint off-map at ${at}`;
    case "solid-terrain":
      return `solid terrain at ${at}`;
    case "occupied":
      if (genericOccupied !== null) return genericOccupied;
      return failure.occupantId ? `occupied by ${failure.occupantId} at ${at}` : `occupied at ${at}`;
  }
}

/**
 * A `move_token` with no `path`: the engine's route ({@link defaultRouteContext}), charged at its
 * cost. Its engagement stop is read off that same route. A Trampler whose every in-budget route
 * crosses a character is refused by name (`landing-zone-full-automation` Q2).
 */
function engineRouteCost(args: {
  state: EncounterState;
  c: Combatant;
  to: Cell;
  budget: number;
}): Result<{ cost: number; stopped: boolean }, CommandError> {
  const { state, c, to, budget } = args;
  const world = worldFor(state);
  const prev = c.pos!;
  // Footprint-aware, server-authoritative validation — the same shared rule the VTT overlay
  // mirrors (`@lancer-vtt/space`'s `moveCostTo`/`reachableAnchors`). The moving combatant never
  // blocks itself; difficult terrain rides `ctx.moveCost`.
  const ctx = defaultRouteContext(world, state, c);
  const cost = moveCostTo({ space: world, from: prev, dest: to, size: c.base.size, budget, ctx });
  if (cost === null) {
    const refusal = trampleRouteRefusal(state, c, to);
    if (refusal !== null) return err(new CommandError(`move_token: ${refusal}`));
    return err(
      new CommandError(
        `move_token: combatant ${c.id} cannot reach ${world.key(to)} from ` +
          `${world.key(prev)} within move budget ${budget} ` +
          // Fog-of-war-safe (never names a concealed blocker): a destination beyond
          // an engagement stop is already unreachable by construction (the space
          // seam's terminal-edge predicate) — folded into this one generic reject,
          // same phrasing whether the cause is off-map, occupied, out of budget, or
          // a stop en route.
          `(off-map, overlapping, out of budget, or stopped by engagement before reaching it)`,
      ),
    );
  }
  // Engagement mid-move stop (RAW Core p.62): reconstruct the actual committed path
  // (the SAME `ctx.stopsAt`-aware A* the overlay preview uses) to ask whether its
  // FINAL step crossed a stopping edge — a newly-engaging anchor. The terminal-edge
  // invariant guarantees this is the
  // only way a stop can be "hiding" inside a reachable `to`: nothing beyond a
  // stopping edge's far cell is ever reachable BY WAY OF it, so if `to` is reachable
  // at all and a stop occurred anywhere en route, `to` itself must BE that stop
  // anchor and the stopping edge must be the path's last one.
  const path = anchorPathTo({ space: world, from: prev, dest: to, size: c.base.size, budget, ctx });
  const stopped =
    path !== null &&
    path.length >= 2 &&
    (ctx.stopsAt?.(path[path.length - 2]!, path[path.length - 1]!) ?? false);
  return ok({ cost, stopped });
}

/**
 * Mesmerized ({@link MESMERIZED_STATUS}): a voluntary move must end closer to the status's source
 * than it began, and a chosen path may not step away from it. `null` when the mover isn't
 * Mesmerized or its source is gone or unplaced.
 */
function mesmerRefusal(args: { state: EncounterState; c: Combatant; from: Cell; to: Cell; path?: readonly Cell[] }): string | null {
  const { state, c, from, to, path } = args;
  const sourceId = c.statuses.find((s) => s.name === MESMERIZED_STATUS)?.source;
  const source = sourceId !== undefined ? state.combatants[sourceId] : undefined;
  if (!source?.pos) return null;
  const world = worldFor(state);
  const sourceCells = world.footprint(source.pos, source.base.size).cells();
  const distance = (at: Cell): number => {
    let best = Infinity;
    for (const a of world.footprint(at, c.base.size).cells()) for (const b of sourceCells) best = Math.min(best, world.distance(a, b));
    return best;
  };
  const stepsAway = (path ?? []).some((cell, i, all) => i > 0 && distance(cell) > distance(all[i - 1]!));
  if (distance(to) >= distance(from) || stepsAway) return `is Mesmerized — its only voluntary movement is toward ${source.name}`;
  return null;
}

/** A `move_token` with a chosen `path` ({@link checkMovePath}), charged at the path's own cost, within `budget`. */
function chosenPathCost(args: { state: EncounterState; c: Combatant; path: readonly Cell[]; budget: number }): Result<{ cost: number; stopped: boolean }, CommandError> {
  const { state, c, path, budget } = args;
  const checked = checkMovePath(state, c, path);
  if (!checked.ok) return err(new CommandError(`move_token: ${checked.error}`));
  if (checked.value.cost > budget) {
    return err(new CommandError(`move_token: combatant ${c.id}'s path costs ${checked.value.cost}, over its move budget ${budget}`));
  }
  return ok(checked.value);
}

/**
 * The GM override's walk (`walk_token`, `decisions/0209`): the path's structure only — it starts on
 * the mover, every step is adjacent and passable, and it ends where the mover can stop — and none of
 * `move_token`'s walls. An engagement stop doesn't end it early; on the mover's own turn it drains
 * the rest of the move budget instead, as a stop would. Off its turn the budget is untouched.
 */
export const applyWalkToken: EncounterHandler<"walk_token"> = (state, cmd) => {
  const cR = requireCombatant(state, cmd.combatantId);
  if (!cR.ok) return cR;
  const c = cR.value;
  const world = worldFor(state);
  const prev = c.pos ?? null;
  if (prev === null) return err(new CommandError(`walk_token: combatant ${c.id} is not on the map — place it with place_token`));
  const to = cmd.path[cmd.path.length - 1];
  if (to === undefined || !world.equals(cmd.path[0]!, prev)) return err(new CommandError(`walk_token: the path must start on ${c.id}'s cell ${world.key(prev)}`));
  const mismatch = cellGridMismatch(encounterGridMode(state), to);
  if (mismatch) return err(new CommandError(`walk_token: ${mismatch}`));
  // The ordinary movement context with no engagement stop: engagement is a rule, not structure.
  const ctx = { ...movementContext(world, state, { exceptId: c.id, bulldozeFollows: true }), stopsAt: undefined };
  const walked = movePathCost({ space: world, path: cmd.path, size: c.base.size, ctx });
  if (!walked.ok) {
    const at = world.key(walked.at);
    const why = walked.cause === "not_adjacent" ? `steps to ${at}, which is not next to the cell before it` : walked.cause === "endpoint_occupied" ? `ends at ${at}, where ${c.id} cannot stop` : `is blocked at ${at}`;
    return err(new CommandError(`walk_token: the path ${why}`));
  }
  const turn = state.activation.turn;
  let nextState: EncounterState = state;
  let inverseActivation: ActivationState | undefined;
  if (turn.phase === "acting" && turn.actor === c.id && cmd.path.length > 1) {
    inverseActivation = state.activation;
    // Engaged on the way: the walk goes on, and the rest of the budget is lost as a stop loses it.
    const engaged = cmd.path.slice(1).some((cell, i) => movementContext(world, state, c.id).stopsAt?.(cmd.path[i]!, cell) === true);
    const move = engaged ? 0 : Math.max(0, turn.budget.move - walked.cost);
    const budget: TurnBudget = { ...turn.budget, move, spent: recordSpend(turn.budget, { move: turn.budget.move - move }) };
    nextState = { ...state, activation: { ...state.activation, turn: { ...turn, budget } } };
  }
  return ok({
    state: withCombatant(nextState, { ...c, pos: to }),
    inverse: { type: "restore_pos", actorId: cmd.actorId, combatantId: c.id, pos: prev, ...(inverseActivation !== undefined ? { activation: inverseActivation } : {}) },
    events: [{ type: "encounter_changed", combatantId: c.id, path: cmd.path }],
  });
};

export const applyMoveToken: EncounterHandler<"move_token"> = (state, cmd) => {
  const cR = requireCombatant(state, cmd.combatantId);
  if (!cR.ok) return cR;
  const c = cR.value;
  const to = cmd.to;
  // The destination is a driver-authored cell — assert it matches the
  // encounter's grid before it can strand in `pos` (a hex cell can never land in
  // a square encounter, or vice versa). Tolerant of an untagged cell.
  const mismatch = cellGridMismatch(encounterGridMode(state), to);
  if (mismatch) return err(new CommandError(`move_token: ${mismatch}`));
  const world = worldFor(state);
  const prev = c.pos ?? null;
  // A no-op move (already at the destination) short-circuits — still recorded
  // (its inverse is a no-op restore), but skips the budget/occupancy work. A `path` that loops
  // back to its start is a real move, and is checked and charged like any other.
  const isNoop = prev !== null && world.equals(prev, to) && cmd.path === undefined;
  if (cmd.path !== undefined && cmd.path.length > 0 && !world.equals(cmd.path[cmd.path.length - 1]!, to)) {
    return err(new CommandError(`move_token: the path must end at the destination ${world.key(to)}`));
  }
  // The whole prior activation, captured into the inverse only when this move
  // CONSUMES move budget (a real move by the acting unit) — so undo restores both
  // `pos` AND the move points the move spent (a partial move is lossy in both). Stays
  // undefined for a budget-exempt deploy / a no-op.
  let inverseActivation: ActivationState | undefined;
  let nextState: EncounterState = state;
  if (!isNoop) {
    // First placement (`pos == null`) is exempt from the move budget (it's deploy,
    // not a move) but still footprint-validated.
    if (prev === null) {
      if (cmd.path !== undefined) return err(new CommandError(`move_token: combatant ${c.id} is not placed yet, and a first placement takes no path`));
      // Fog-of-war (cli-invalid-action-errors D6): off-map and solid terrain are named
      // precisely — both are already drawn on every client's board. Occupied stays the
      // generic "overlapping another token" and never names the blocker: a player can
      // trigger this reject at will by walking into a concealed unit's cell.
      // D8: the trailing place_token pointer is load-bearing — it is the signpost from
      // `move`'s deploy branch to `place` (which owns any nearest-valid search). Keep it.
      // A first placement is an ENDPOINT, so it must see full occupancy —
      // `blocked ∪ noStop` — via the named endpoint predicate, never `blocked` alone
      // (that would let a token land on an ally/smaller-hostile it may only pass
      // THROUGH).
      const failure = placementFailure(state, { mover: c.id, size: c.base.size, anchor: to });
      if (failure) {
        return err(
          new CommandError(
            `move_token: combatant ${c.id} cannot be placed at ${world.key(to)} ` +
              `(${placementCauseText(world, failure, "overlapping another token")}; ` +
              `deploy it to a clear cell, or place it out of turn via place_token, whose --nearest picks the closest clear cell)`,
          ),
        );
      }
    } else {
      // Activation wall (RAW Core p.60 — a unit makes its move during ITS OWN
      // activation). A real PLAY move is legal only for the unit currently mid-turn
      // (`turn.phase === "acting" && turn.actor === c.id`); every other unit's
      // `move_token` is a hard reject — and when NOBODY is mid-turn (`turn.phase ===
      // "idle"`: a fresh fight before the first activation, or the gap between turns)
      // no unit may PLAY-move at all.
      // There is no "free movement when nobody's active" state — to move a unit in
      // PLAY you activate it first. This is the activation twin of the hard move
      // budget below, and role-agnostic like every reducer rule (the wall is the
      // same for GM and player). Out-of-turn repositioning (setup, a GM fudge,
      // shuffling a non-active unit) is EDIT `place_token`, the budget-and-
      // activation-exempt authoring escape, never `move_token`.
      // Deploy (`prev === null`) never reaches here — a placement is not a move
      // (see the branch above). Checked as the raw phase/actor test (not
      // `mayPlayMove`, which ALSO folds in the status bar below) so this
      // reject's "whose turn is it" phrasing stays reserved for an actual
      // turn mismatch — see the split immediately below.
      const actingTurn = state.activation.turn;
      if (actingTurn.phase !== "acting" || actingTurn.actor !== c.id) {
        return err(
          new CommandError(
            `move_token: combatant ${c.id} cannot move while it is ` +
              `${activeActor(state.activation) ?? "nobody"}'s turn (only the active unit moves; ` +
              `reposition a non-active unit out of turn via place_token)`,
          ),
        );
      }
      // Status-driven voluntary-movement gate (RAW pp.77-78,
      // `voluntaryMoveBar` — the same wall `mayPlayMove` folds in for the
      // overlay's reach painters). IS this unit's turn (the check above passed) but
      // it carries Immobilized/Stunned/Shut Down/Down and Out, so a voluntary move is
      // still illegal — read the bar's OWN reason string rather than reusing the
      // "not your turn" phrasing above (which would be wrong here: it IS this unit's
      // turn, it just can't voluntarily move). Fog-of-war-safe: names only the mover,
      // same discipline as every reject in this case.
      const bar = voluntaryMoveBar(c);
      if (bar) {
        return err(new CommandError(`move_token: combatant ${c.id} cannot voluntarily move — ${bar}`));
      }
      // The braced turn mints no standard move (`freshTurnBudget`); say so rather than "out of budget".
      if (onBracedTurn(c, state.activation) && moveBudgetFor(state, c) <= 0) {
        return err(new CommandError(`move_token: ${c.name} Braced last turn and can't move normally this turn (RAW p.73)`));
      }
      const mesmer = mesmerRefusal({ state, c, from: prev, to, path: cmd.path });
      if (mesmer) return err(new CommandError(`move_token: combatant ${c.id} ${mesmer}`));
      const tether = tetherBreaker(state, c, to);
      if (tether) return err(new CommandError(`move_token: combatant ${c.id} is tethered to ${tether.name} by a cable winch — it can't end more than ${TETHER_RANGE} spaces away`));
      // A standard move mid-attack-lock is DELIBERATELY allowed: the
      // acting actor may walk between the shots of an open attack (a Barrage, or a lock-open
      // Skirmish awaiting/mid its Aux rider). This is what makes the Ordnance sequence
      // `barrage → fire ordnance opener → move → fire secondary` legal (RAW Core Ordnance tag,
      // p.105: an Ordnance weapon fires "before the mech moves or takes any other action... [it]
      // can still act and move normally after attacking"). The move is the standard move, NOT an
      // action — the other actions the lock blocks (Overcharge / free activations / Stabilize /
      // Full Tech / an ineligible second Skirmish weapon) stay blocked upstream in
      // `encounterRoom.ts`'s mid-attack gate, which catches those INTENTS.
      // Ordnance ORDERING is still enforced by `checkOrdnance`: an opener reads the LIVE window
      // (so `move → barrage with an Ordnance opener` stays rejected — the move already spent the
      // virgin window), while a continuation reads the lock's `ordnanceWindow` SNAPSHOT frozen at
      // `open_attack` (so a continuation after a mid-lock move stays legal — the Barrage is one
      // action, judged at its start). The move rides the normal path below: budget, engagement,
      // overwatch/reactions, MOVE_LISTENER_RECOMPUTERS/SETTLE_RECOMPUTERS all fire as usual.
      // Held-action lockout (RAW Core pp. 71-72): a
      // combatant holding a prepared action "cannot move, act, or take reactions" until
      // the trigger fires or its own next turn starts. Scoped to the mover's OWN hold
      // (the acting-actor legality path) — another unit's move is unaffected. The
      // force-apply `place_token` (out of band, edit mode) is unaffected, mirroring the
      // attack lock's own scope just above.
      if (heldAction(state.activation, c.id)) {
        return err(
          new CommandError(
            `move_token: combatant ${c.id} is holding a prepared action — drop it (drop_prepare) ` +
              `or wait for it to trigger/lapse before moving`,
          ),
        );
      }
      // Flight heading lock: a Flying mover's committed heading — the FIRST flight
      // segment of this activation (or the first since a `boost` cleared it) —
      // constrains every SUBSEQUENT flight segment to its line. Only relevant to a
      // Flying mover with a heading ALREADY locked; the segment that establishes the
      // heading is unconstrained here (checked below, at the budget commit, once this
      // move is otherwise known legal). A non-flying (walking) move never consults
      // `turn.heading` at all — RAW imposes no heading on it.
      const lockedHeading =
        actingTurn.phase === "acting" && hasStatus(c.statuses, FLYING_STATUS) ? actingTurn.heading : undefined;
      if (lockedHeading && !flightHeadingAllows({ world, heading: lockedHeading, from: prev, to })) {
        return err(
          new CommandError(
            `move_token: combatant ${c.id} is Flying and locked to a heading from this ` +
              `activation's first flight segment — ${world.key(to)} isn't on that straight line from ` +
              `${world.key(prev)} (RAW p.63: flight must follow a straight line; Boost to unlock a new heading)`,
          ),
        );
      }
      // A real move is bounded by the combatant's REMAINING per-activation move
      // budget, not full Speed from the current cell —
      // so a unit can't out-move its Speed by re-issuing from each new cell. The
      // remaining budget is the acting unit's `turn.budget.move` (the `mayPlayMove`
      // wall above already guaranteed this unit is the one acting), read via
      // `moveBudgetFor` — the one place the rule lives, shared with the overlay.
      const budget = moveBudgetFor(state, c);
      const walked = cmd.path !== undefined ? chosenPathCost({ state, c, path: cmd.path, budget }) : engineRouteCost({ state, c, to, budget });
      if (!walked.ok) return err(walked.error);
      const { cost, stopped } = walked.value;
      // Decrement the acting unit's per-turn move budget by the path cost — or, on a
      // stop, ZERO it outright ("lose any unused movement" maps onto the
      // fused per-activation pool as a full zero, not a partial charge; a Boost taken
      // AFTER the stop grants a fresh Speed and can itself be stopped again). The
      // `mayPlayMove` wall above guarantees `turn` is `acting` with `actor === c.id`, so
      // the budget exists and belongs to this unit — no fallback branch.
      const turn = state.activation.turn;
      if (turn.phase === "acting" && turn.actor === c.id) {
        inverseActivation = state.activation;
        const move = stopped ? 0 : turn.budget.move - cost;
        // The cost-keyed spend counter tracks
        // what actually left the pool — `cost` normally, the WHOLE remaining pool on a
        // stop (the zero-outright branch above). Derived from the two budgets rather than
        // re-deriving the branch, so the counter can't drift from the decrement it mirrors.
        const budget: TurnBudget = { ...turn.budget, move, spent: recordSpend(turn.budget, { move: turn.budget.move - move }) };
        // Flight heading lock: a Flying mover's FIRST flight
        // segment this activation (`turn.heading` still absent — the check above
        // only rejects when a heading is ALREADY locked) establishes it from this
        // segment's own endpoints; every later segment leaves it untouched (the
        // legality check above already proved it's on-line, so there's nothing to
        // update). A non-flying mover's `turn.heading` (normally absent) just rides
        // through unchanged.
        const heading: FlightHeading | undefined = hasStatus(c.statuses, FLYING_STATUS)
          ? (turn.heading ?? { origin: prev, dest: to })
          : turn.heading;
        nextState = {
          ...state,
          activation: {
            // Spread the existing acting `turn` so its `attack` lock (and any other acting-turn
            // field) survives the move — a standard move mid-attack-lock is legal, and the lock
            // must persist across it so the Barrage/Skirmish continuation still reads its
            // snapshot. Only `budget`/`heading` are recomputed here.
            ...state.activation,
            turn: { ...turn, budget, ...(heading !== undefined ? { heading } : {}) },
          },
        };
      }
    }
  }
  const next = withCombatant(nextState, { ...c, pos: to });
  // Inverse: always a `restore_pos` to the captured prior `pos` (which may be
  // `null` so undo of a first placement round-trips the unplaced state — a
  // plain move_token back can't express null), carrying the prior budget when the
  // move consumed it (so undo of a partial move round-trips the move points too).
  return ok({
    state: next,
    inverse: {
      type: "restore_pos",
      actorId: cmd.actorId,
      combatantId: c.id,
      pos: prev,
      ...(inverseActivation !== undefined ? { activation: inverseActivation } : {}),
    },
    events: cmd.path !== undefined ? [{ type: "encounter_changed", combatantId: c.id, path: cmd.path }] : changed(c.id),
  });
};

export const applyPlaceToken: EncounterHandler<"place_token"> = (state, cmd) => {
  // Authoring placement (edit mode): footprint-fit only, no speed/path budget.
  // Keeps the *structural* invariants `move_token` enforces
  // — grid match + footprint (on-map, non-overlapping) — and drops only the
  // *tactical* tier (reachability within Speed). GM-only at the gate.
  const cR = requireCombatant(state, cmd.combatantId);
  if (!cR.ok) return cR;
  const c = cR.value;
  const to = cmd.to;
  const mismatch = cellGridMismatch(encounterGridMode(state), to);
  if (mismatch) return err(new CommandError(`place_token: ${mismatch}`));
  const world = worldFor(state);
  const prev = c.pos ?? null;
  const isNoop = prev !== null && world.equals(prev, to);
  if (!isNoop) {
    // The moving combatant never blocks itself; terrain-solid + other tokens do.
    // Every cause is named, the occupant included: `place_token` is GM-only at the
    // permission gate (`permissions.ts`), so no player can reach this reject (D6).
    // `place_token` is a pure
    // ENDPOINT relocation — full occupancy (`blocked ∪ noStop`) via the named
    // endpoint predicate, so this force-apply command can never stack a token
    // onto an ally/smaller-hostile it would only be allowed to travel through.
    const failure = placementFailure(state, { mover: c.id, size: c.base.size, anchor: to });
    if (failure) {
      return err(
        new CommandError(
          `place_token: combatant ${c.id} cannot be placed at ${world.key(to)} (${placementCauseText(world, failure, null)})`,
        ),
      );
    }
  }
  const next = withCombatant(state, { ...c, pos: to });
  // Same inverse as move_token — a `restore_pos` to the captured prior `pos`
  // (which may be `null`, so undo of a first authoring placement round-trips).
  return ok({
    state: next,
    inverse: { type: "restore_pos", actorId: cmd.actorId, combatantId: c.id, pos: prev },
    events: changed(c.id),
  });
};

export const applyTeleportToken: EncounterHandler<"teleport_token"> = (state, cmd) => {
  // Teleport (RAW Core p.63) — the third movement
  // peer to `move_token`/`place_token` (see `TeleportTokenCommand`'s doc comment for
  // the full RAW trail). Never legal for an unplaced combatant — teleport
  // moves an already-deployed unit; deploying one is `move_token`/`place_token`'s job.
  const cR = requireCombatant(state, cmd.combatantId);
  if (!cR.ok) return cR;
  const c = cR.value;
  const to = cmd.to;
  const mismatch = cellGridMismatch(encounterGridMode(state), to);
  if (mismatch) return err(new CommandError(`teleport_token: ${mismatch}`));
  if (c.pos === null || c.pos === undefined) {
    return err(
      new CommandError(
        `teleport_token: combatant ${c.id} is not yet placed — deploy it first (move_token/place_token)`,
      ),
    );
  }
  const prev = c.pos;
  const world = worldFor(state);
  const isNoop = world.equals(prev, to);
  let inverseActivation: ActivationState | undefined;
  let nextState: EncounterState = state;
  if (!isNoop) {
    // Activation wall (RAW Core p.60 — a unit makes its move during ITS OWN activation),
    // the same split `move_token` uses (a raw phase/actor test, not `mayPlayMove`, so
    // this reject's "whose turn is it" phrasing stays reserved for an actual mismatch).
    const actingTurn = state.activation.turn;
    if (actingTurn.phase !== "acting" || actingTurn.actor !== c.id) {
      return err(
        new CommandError(
          `teleport_token: combatant ${c.id} cannot teleport while it is ` +
            `${activeActor(state.activation) ?? "nobody"}'s turn (only the active unit teleports)`,
        ),
      );
    }
    // Status-driven voluntary-movement gate (`voluntaryMoveBar` — Immobilized/Stunned/
    // Shut Down/Down and Out). RAW p.63: teleporting "still counts as movement and so is
    // affected by conditions like IMMOBILIZED."
    const bar = voluntaryMoveBar(c);
    if (bar) {
      return err(new CommandError(`teleport_token: combatant ${c.id} cannot voluntarily move — ${bar}`));
    }
    // The frozen range wall (capabilities over kinds): no capability,
    // no teleport — a caller-supplied range would be no wall at all.
    const teleport = c.base.teleport;
    if (!teleport) {
      return err(new CommandError(`teleport_token: combatant ${c.id} has no teleport capability`));
    }
    // Range — footprint-to-footprint (`regionDistance`, the same measure the drag
    // adjacency check and `combatantRange` use), against the frozen `range`.
    const fromFootprint = cellRegion(world.footprint(prev, c.base.size).cells(), world.key);
    const toFootprint = cellRegion(world.footprint(to, c.base.size).cells(), world.key);
    const dist = world.regionDistance(fromFootprint, toFootprint);
    if (dist > teleport.range) {
      return err(
        new CommandError(
          `teleport_token: destination ${world.key(to)} is out of teleport range ` +
            `(${dist} > ${teleport.range})`,
        ),
      );
    }
    // Endpoint gate — free, no LoS required (RAW: "can attempt to teleport to spaces
    // they can't see, but if a space is already occupied, the teleport fails"). No A*:
    // teleport has no path, so no obstruction/engagement pass-through to validate — only
    // the destination footprint itself must be clear of BOTH `blocked` and `noStop`.
    // Fog-of-war (D6): off-map and solid terrain named; occupied stays generic.
    const failure = placementFailure(state, { mover: c.id, size: c.base.size, anchor: to });
    if (failure) {
      return err(
        new CommandError(
          `teleport_token: combatant ${c.id} cannot teleport to ${world.key(to)} (${placementCauseText(world, failure, "occupied")})`,
        ),
      );
    }
    // Budget — a flat 1 space, always (RAW p.63: "counts as moving 1 space, no matter
    // how far they travel"), regardless of the distance actually travelled.
    const budget = moveBudgetFor(state, c);
    if (budget < 1) {
      return err(
        new CommandError(`teleport_token: combatant ${c.id} has no move budget remaining to teleport with`),
      );
    }
    const turn = state.activation.turn;
    if (turn.phase === "acting" && turn.actor === c.id) {
      inverseActivation = state.activation;
      // The flat 1 space comes off the pool, so it goes onto the spend counter too — one
      // move point spent, no matter how far the teleport actually travelled.
      const nextBudget: TurnBudget = { ...turn.budget, move: turn.budget.move - 1, spent: recordSpend(turn.budget, { move: 1 }) };
      nextState = { ...state, activation: { ...state.activation, turn: { phase: "acting", actor: c.id, budget: nextBudget } } };
    }
  }
  const next = withCombatant(nextState, { ...c, pos: to });
  // Same inverse shape as `move_token`: a `restore_pos` to the captured prior `pos`,
  // carrying the prior activation when the teleport consumed the flat 1-space budget.
  return ok({
    state: next,
    inverse: {
      type: "restore_pos",
      actorId: cmd.actorId,
      combatantId: c.id,
      pos: prev,
      ...(inverseActivation !== undefined ? { activation: inverseActivation } : {}),
    },
    events: changed(c.id),
  });
};

export const applyRestorePos: EncounterHandler<"restore_pos"> = (state, cmd) => {
  // Internal inverse of move_token: set `pos` directly to a captured value
  // (possibly `null`). Unvalidated — it only ever replays a state the
  // encounter was already in, so it must round-trip past an intervening edit
  // that would now fail forward validation. When `cmd.activation` is set (the inverse
  // of a budget-consuming move), also restore the whole `activation` wholesale — and
  // capture the pre-restore activation into its own inverse so redo of the move
  // re-spends the move points (a partial move is lossy in both `pos` and budget). A
  // pos-only restore (no `cmd.activation`) leaves the activation untouched.
  const cR = requireCombatant(state, cmd.combatantId);
  if (!cR.ok) return cR;
  const c = cR.value;
  const prev = c.pos ?? null;
  const moved = withCombatant(state, { ...c, pos: cmd.pos });
  const next: EncounterState =
    cmd.activation !== undefined ? { ...moved, activation: cmd.activation } : moved;
  return ok({
    state: next,
    inverse: {
      type: "restore_pos",
      actorId: cmd.actorId,
      combatantId: c.id,
      pos: prev,
      ...(cmd.activation !== undefined ? { activation: state.activation } : {}),
    },
    events: changed(c.id),
  });
};

export const applyStandUp: EncounterHandler<"stand_up"> = (state, cmd) => {
  // Prone stand-up (RAW Core p.77): a rules-following reducer command, hard-enforced
  // like `move_token`/`boost` — NOT
  // a force-apply override. Legality (all must hold):
  //   1. Acting turn — only the unit currently mid-turn may stand.
  //   2. Carries Prone — nothing to stand from otherwise.
  //   3. NOT Immobilized (RAW-explicit: "unless they're IMMOBILIZED") — its own distinct
  //      message, checked BEFORE the Stunned/D&O arm below so an Immobilized carrier is
  //      never double-reported.
  //   4. Not barred by `voluntaryMoveBar`'s Stunned/Shut Down/Down and Out arm (checked
  //      directly here, not via the shared predicate, since that predicate ALSO covers
  //      Immobilized — already handled by #3 with its own message above).
  //   5. The standard move is UNSPENT (`turn.budget.move === base.speed`) — the
  //      deliberately strict fork: a carrier that already crawled this turn is
  //      rejected, not softened.
  const cR = requireCombatant(state, cmd.combatantId);
  if (!cR.ok) return cR;
  const c = cR.value;
  const a = state.activation;
  if (a.turn.phase !== "acting" || a.turn.actor !== c.id) {
    return err(
      new CommandError(
        `stand_up: combatant ${c.id} cannot stand while it is ` +
          `${activeActor(a) ?? "nobody"}'s turn (only the active unit may stand up)`,
      ),
    );
  }
  if (!hasStatus(c.statuses, PRONE_STATUS)) {
    return err(new CommandError(`stand_up: combatant ${c.id} is not Prone — nothing to stand up from`));
  }
  if (hasStatus(c.statuses, IMMOBILIZED_STATUS)) {
    return err(new CommandError(`stand_up: combatant ${c.id} is Immobilized — cannot stand up (RAW p.77-78)`));
  }
  if (isStunned(c) || hasStatus(c.statuses, DOWN_AND_OUT_STATUS)) {
    return err(
      new CommandError(
        `stand_up: combatant ${c.id} is Stunned (or Shut Down/Down and Out) — cannot voluntarily move, no standing up (RAW p.78)`,
      ),
    );
  }
  const turn = a.turn;
  // AUDITED, sound as written — do not "fix" this to the spend counter. It is the same
  // remaining-budget proxy `turns.ts`'s `movedNothing` had to abandon (a grant reads as a
  // spend), but the hole cannot reach here: Boost is the only thing that raises
  // `budget.move` above `base.speed`, and Boost is rejected while Prone
  // (`handlers/budget.ts`'s Slowed/Prone wall), which is the only state this gate runs in.
  if (turn.budget.move !== c.base.speed) {
    return err(
      new CommandError(
        `stand_up: combatant ${c.id} has already spent part of its standard move ` +
          `(budget ${turn.budget.move}/${c.base.speed}) — standing up is only legal before ` +
          `any movement this turn (decisions/0065)`,
      ),
    );
  }
  // Effect: remove the exact Prone instance, zero the pool (never touch `pos` — see the
  // command's doc comment for why that alone suppresses MOVE_LISTENER_RECOMPUTERS/SETTLE_RECOMPUTERS), never
  // touch `budget.actions` (a subsequent Boost still grants a fresh Speed).
  const prevStatuses = c.statuses;
  const nextStatuses = c.statuses.filter((s) => s.name !== PRONE_STATUS);
  // Standing up consumes the standard move, so the whole pool it zeroes lands on the spend
  // counter. **Not one of the four sites `projects/action-ordering-audit.md` D2 enumerates** —
  // D2's list missed it (and `prepare`'s action-point charge). Left out, this would be a
  // move-budget decrement the counter can't see, and Phase 2's `movedNothing` →
  // `spent.move === 0` would then ground a Flying unit that stood up, which today's
  // `budget.move === base.speed` read does not. Including it keeps that behavior identical.
  // The guard above pins `turn.budget.move === c.base.speed`, so this is exactly one Speed.
  const budget: TurnBudget = { ...turn.budget, move: 0, spent: recordSpend(turn.budget, { move: turn.budget.move }) };
  const nextActivation: ActivationState = { ...a, turn: { phase: "acting", actor: c.id, budget } };
  const next = withCombatant({ ...state, activation: nextActivation }, { ...c, statuses: nextStatuses });
  return ok({
    state: next,
    inverse: {
      type: "restore_stand",
      actorId: cmd.actorId,
      combatantId: c.id,
      statuses: prevStatuses,
      activation: a,
    },
    events: changed(c.id),
  });
};

export const applyRestoreStand: EncounterHandler<"restore_stand"> = (state, cmd) => {
  // Internal, unvalidated inverse of `stand_up` — and its own inverse (see the command's
  // doc comment: a REDO of a `stand_up` replays forward through another `restore_stand`,
  // never a re-validated `stand_up`). Sets the combatant's `statuses` and the whole
  // `activation` directly to the captured snapshot; both captured wholesale so the exact
  // Prone `StatusInstance` and the exact prior `turn.budget.move` round-trip losslessly.
  const cR = requireCombatant(state, cmd.combatantId);
  if (!cR.ok) return cR;
  const c = cR.value;
  const prevStatuses = c.statuses;
  const prevActivation = state.activation;
  const next = withCombatant({ ...state, activation: cmd.activation }, { ...c, statuses: cmd.statuses });
  return ok({
    state: next,
    inverse: {
      type: "restore_stand",
      actorId: cmd.actorId,
      combatantId: c.id,
      statuses: prevStatuses,
      activation: prevActivation,
    },
    events: changed(c.id),
  });
};
